import { Box, Group, Text } from '@mantine/core'
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  type MutableRefObject,
  type RefObject,
} from 'react'

import type { BoggleRunSummaryBadgeLine } from '~/boggle/config/runSummaryScore'
import { BadgeMedallion } from '~/boggle/ui/compendium/BadgeMedallion'
import { BadgePopover } from '~/boggle/ui/compendium/BadgePopover'
import {
  BOGGLE_BADGE_FLY_DURATION_MS,
  type BoggleBadgeRevealPhase,
} from '~/boggle/ui/run/useRunResultsChoreography'

import classes from './BoggleBadgeRevealStage.module.css'

const SLOT_SIZE_PX = 46
const SLOT_GAP_PX = 10

/**
 * Pure helper: horizontal offset (in px) of slot `index` in a single-row
 * centered list (legacy math; tests document expected spacing constants).
 */
export function computeBoggleBadgeListSlotOffsetPx({
  count,
  index,
}: {
  count: number
  index: number
}): number {
  return (index - (count - 1) / 2) * (SLOT_SIZE_PX + SLOT_GAP_PX)
}

/**
 * Pure helper: from the per-badge phase array, return both the centered list
 * order (badges currently `flying` or `settled`) and the index of the badge
 * currently `revealing` at the bottom (or -1 when none).
 */
export function partitionBoggleBadgeRevealStage(
  phases: readonly BoggleBadgeRevealPhase[],
): {
  inListBadgeIndices: number[]
  revealingBadgeIndex: number
} {
  const inListBadgeIndices: number[] = []
  let revealingBadgeIndex = -1
  for (let i = 0; i < phases.length; i++) {
    const phase = phases[i]
    if (phase === 'flying' || phase === 'settled') {
      inListBadgeIndices.push(i)
    } else if (phase === 'revealing' && revealingBadgeIndex === -1) {
      revealingBadgeIndex = i
    }
  }
  return { inListBadgeIndices, revealingBadgeIndex }
}

/**
 * Badge row below the tier metres: wrapping flex row, centered. Slots that
 * were already in the list animate to new flex positions via FLIP when the
 * row reflows; first-time entries still fly in from the bottom reveal anchor
 * (`bottomAnchorRef`).
 */
export function BoggleBadgeList({
  badgeLines,
  bottomAnchorRef,
  phases,
}: {
  badgeLines: readonly BoggleRunSummaryBadgeLine[]
  bottomAnchorRef: RefObject<HTMLDivElement | null>
  phases: readonly BoggleBadgeRevealPhase[]
}) {
  const { inListBadgeIndices } = useMemo(
    () => partitionBoggleBadgeRevealStage(phases),
    [phases],
  )

  const listRef = useRef<HTMLDivElement>(null)
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const slotsReadyRef = useRef<Set<number>>(new Set())
  const inListLayoutKeyRef = useRef('')
  const listLayoutRectsRef = useRef<
    Map<number, { left: number; top: number; width: number; height: number }>
  >(new Map())

  useLayoutEffect(() => {
    const ready = slotsReadyRef.current
    const prevRects = listLayoutRectsRef.current
    const listEl = listRef.current

    const nowRects = new Map<
      number,
      { left: number; top: number; width: number; height: number }
    >()
    if (listEl) {
      for (const badgeIndex of inListBadgeIndices) {
        const el = slotRefs.current.get(badgeIndex)
        if (!el) {
          continue
        }
        /** Layout box only — `getBoundingClientRect` includes the outer FLIP
         * translate, so remeasuring mid-animation fights the in-flight tween. */
        nowRects.set(badgeIndex, {
          left: el.offsetLeft,
          top: el.offsetTop,
          width: el.offsetWidth,
          height: el.offsetHeight,
        })
      }
    }

    const inListLayoutKey = inListBadgeIndices.join(',')
    const listMembershipChanged = inListLayoutKey !== inListLayoutKeyRef.current
    inListLayoutKeyRef.current = inListLayoutKey

    /** Sibling shuffle when flex reflows (new badge joined or wrap line shifts).
     * Skip badges still in `flying`: inner translate/scale is animating and
     * distorts bbox — FLIP deltas would fight the fly-in and read as jitter. */
    const repositioned = new Set<number>()
    if (listMembershipChanged) {
      for (const badgeIndex of inListBadgeIndices) {
        if (phases[badgeIndex] === 'flying') {
          continue
        }
        const prev = prevRects.get(badgeIndex)
        const last = nowRects.get(badgeIndex)
        const el = slotRefs.current.get(badgeIndex)
        if (!prev || !last || !el) {
          continue
        }
        const ix = prev.left - last.left
        const iy = prev.top - last.top
        if (Math.abs(ix) < 0.5 && Math.abs(iy) < 0.5) {
          continue
        }
        el.style.transition = 'none'
        el.style.transform = `translate(${ix}px, ${iy}px)`
        repositioned.add(badgeIndex)
      }
    }

    const flyInBadges: number[] = []

    for (const badgeIndex of inListBadgeIndices) {
      if (ready.has(badgeIndex)) {
        continue
      }
      const slot = slotRefs.current.get(badgeIndex)
      if (!slot) {
        continue
      }

      const bottom = bottomAnchorRef.current

      if (!bottom) {
        ready.add(badgeIndex)
        continue
      }

      const layout = nowRects.get(badgeIndex)
      const bottomRect = bottom.getBoundingClientRect()
      if (!layout || !listEl || layout.width === 0 || bottomRect.width === 0) {
        ready.add(badgeIndex)
        continue
      }

      const listRect = listEl.getBoundingClientRect()
      const slotCenterX = listRect.left + layout.left + layout.width / 2
      const slotCenterY = listRect.top + layout.top + layout.height / 2
      const bottomCenterX = bottomRect.left + bottomRect.width / 2
      const bottomCenterY = bottomRect.top + bottomRect.height / 2
      const dx = bottomCenterX - slotCenterX
      const dy = bottomCenterY - slotCenterY
      const scaleRatio = bottomRect.width / layout.width

      const translateEl = slot.querySelector<HTMLElement>('[data-boggle-fly="translate"]')
      const scaleEl = slot.querySelector<HTMLElement>('[data-boggle-fly="scale"]')

      if (!translateEl || !scaleEl) {
        continue
      }

      translateEl.style.transition = 'none'
      scaleEl.style.transition = 'none'
      translateEl.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
      scaleEl.style.transform = `scale(${scaleRatio})`
      flyInBadges.push(badgeIndex)
      ready.add(badgeIndex)
    }

    if (repositioned.size > 0 || flyInBadges.length > 0) {
      const probe = slotRefs.current.get(inListBadgeIndices[0] ?? -1) ?? undefined
      if (probe) {
        void probe.offsetWidth
      }
    }

    requestAnimationFrame(() => {
      for (const badgeIndex of repositioned) {
        const el = slotRefs.current.get(badgeIndex)
        if (!el) {
          continue
        }
        el.style.transition = `transform ${BOGGLE_BADGE_FLY_DURATION_MS}ms ease`
        el.style.transform = 'none'
      }
      for (const badgeIndex of flyInBadges) {
        const slot = slotRefs.current.get(badgeIndex)
        if (!slot) {
          continue
        }
        const translateEl = slot.querySelector<HTMLElement>(
          '[data-boggle-fly="translate"]',
        )
        const scaleEl = slot.querySelector<HTMLElement>('[data-boggle-fly="scale"]')
        if (!translateEl || !scaleEl) {
          continue
        }
        const flyEasing = `transform ${BOGGLE_BADGE_FLY_DURATION_MS}ms linear`
        translateEl.style.transition = flyEasing
        scaleEl.style.transition = flyEasing
        translateEl.style.transform = 'none'
        scaleEl.style.transform = 'none'
      }
    })

    if (listMembershipChanged) {
      listLayoutRectsRef.current = nowRects
    }

    for (const badgeIndex of Array.from(ready)) {
      if (!inListBadgeIndices.includes(badgeIndex)) {
        ready.delete(badgeIndex)
      }
    }
  }, [bottomAnchorRef, inListBadgeIndices, phases])

  const setSlotRef = useCallback(
    (badgeIndex: number) => (el: HTMLDivElement | null) => {
      if (el) {
        slotRefs.current.set(badgeIndex, el)
      } else {
        slotRefs.current.delete(badgeIndex)
      }
    },
    [],
  )

  return (
    <Group
      ref={listRef}
      gap={10}
      className={classes.list}
      data-testid="boggle-badge-reveal-list"
      justify="center"
      wrap="wrap"
    >
      {inListBadgeIndices.map(badgeIndex => {
        const line = badgeLines[badgeIndex]
        if (!line) {
          return null
        }
        return (
          <div
            className={classes.slot}
            data-badge-id={line.badge.id}
            data-testid={`boggle-badge-reveal-slot-${line.badge.id}`}
            key={`${line.badge.id}:${line.earnedAtLedgerIndex}`}
            ref={setSlotRef(badgeIndex)}
          >
            <div className={classes.flyTranslate} data-boggle-fly="translate">
              <div className={classes.flyScale} data-boggle-fly="scale">
                <BadgePopover
                  countsByRating={{}}
                  definition={line.badge}
                  frameTriggerWhenOpen
                  p={4}
                >
                  <BadgeMedallion
                    definition={line.badge}
                    highestEarnedRating={null}
                    locked={false}
                    size="sm"
                  />
                </BadgePopover>
              </div>
            </div>
          </div>
        )
      })}
    </Group>
  )
}

/**
 * The bottom reveal pad rendered just above the action buttons. Owns the
 * large reveal medallion + name for the badge in `revealing`, and an always-mounted
 * positional anchor (`bottomAnchorRef`) that the BadgeList uses as the FLIP
 * source for newly-added slots.
 *
 * The tag-anchor `<div>` (`badgeAnchorsRef[index]`) is a 1×1 element placed
 * at the upper-right of the reveal medallion so the floating score tag spawns
 * from that corner.
 */
export function BoggleBadgeRevealPad({
  badgeAnchorsRef,
  badgeLines,
  bottomAnchorRef,
  phases,
}: {
  badgeAnchorsRef?: MutableRefObject<(HTMLDivElement | null)[]>
  badgeLines: readonly BoggleRunSummaryBadgeLine[]
  bottomAnchorRef: RefObject<HTMLDivElement | null>
  phases: readonly BoggleBadgeRevealPhase[]
}) {
  const { revealingBadgeIndex } = useMemo(
    () => partitionBoggleBadgeRevealStage(phases),
    [phases],
  )

  const setTagAnchorRef = useCallback(
    (badgeIndex: number) => (el: HTMLDivElement | null) => {
      if (badgeAnchorsRef) {
        badgeAnchorsRef.current[badgeIndex] = el
      }
    },
    [badgeAnchorsRef],
  )

  const revealingLine = revealingBadgeIndex >= 0 ? badgeLines[revealingBadgeIndex] : null

  return (
    <Box className={classes.bottom} data-testid="boggle-badge-reveal-pad">
      <div
        className={classes.bottomAnchor}
        // `bottomAnchorRef` typed as `RefObject` (read-only `.current`) at the
        // call site; assigning via `as MutableRefObject` is the standard pattern
        // when the same ref is read by a sibling component.
        ref={bottomAnchorRef as MutableRefObject<HTMLDivElement | null>}
      >
        {revealingLine ? (
          <div
            className={`${classes.bottomMedallion} ${classes.bottomMedallionEnter}`}
            data-testid={`boggle-badge-reveal-bottom-${revealingLine.badge.id}`}
            key={`${revealingLine.badge.id}:${revealingLine.earnedAtLedgerIndex}:reveal`}
          >
            <BadgePopover
              countsByRating={{}}
              definition={revealingLine.badge}
              frameTriggerWhenOpen
              p={4}
            >
              <BadgeMedallion
                definition={revealingLine.badge}
                highestEarnedRating={null}
                locked={false}
                size="lg"
              />
            </BadgePopover>
            <div
              aria-hidden
              className={classes.tagAnchor}
              ref={setTagAnchorRef(revealingBadgeIndex)}
            />
          </div>
        ) : null}
      </div>
      <Group justify="center" mt={6} h={22} style={{ pointerEvents: 'none' }} w="100%">
        {revealingLine ? (
          <Text className={classes.bottomName}>{revealingLine.badge.name}</Text>
        ) : null}
      </Group>
    </Box>
  )
}
